import { useImmerAtom } from "jotai-immer";
import { isEmpty } from "lodash-es";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import ArrowRightRoundedIcon from "@mui/icons-material/ArrowRightRounded";
import TranslateRoundedIcon from "@mui/icons-material/TranslateRounded";
import {
  Divider,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Typography,
} from "@mui/material";

import { Settings } from "@baiki/interfaces";

import { Language, languages } from "@/config";
import { FeaturesHooks } from "@/hooks";
import { ListItemButtonMenu } from "@/primitives/ListItemButtonMenu";
import { Storage } from "@/store";

const supportedLanguages: Language[] = languages.filter((l) => !l.beta);
const betaLanguages: Language[] = languages.filter((l) => l.beta);

export const LanguageSetting = () => {
  const { t, i18n } = useTranslation();

  const { canSeeBetaLanguages } = FeaturesHooks.useFeatureFlags();

  const [settings, setSettings] = useImmerAtom(Storage.settings);

  const [open, setOpen] = useState(false);

  const handleLanguageChange = (language: string) => {
    i18n.changeLanguage(language);
    setSettings((store: Settings) => {
      store.language = language;
    });
    localStorage.setItem("baiki-language", language);
    setOpen(false);
  };

  const showBetaLanguanges = useMemo(() => {
    return canSeeBetaLanguages && !isEmpty(betaLanguages);
  }, [canSeeBetaLanguages]);

  return (
    <ListItemButtonMenu
      menuProps={{
        anchorOrigin: { horizontal: "right", vertical: "center" },
        onClose: () => setOpen(false),
        open,
        transformOrigin: { horizontal: "right", vertical: "top" },
        slotProps: {
          paper: { sx: (theme) => ({ marginTop: theme.spacing(2) }) },
        },
      }}
      onClick={() => setOpen(true)}
      menu={[
        ...supportedLanguages.map((l) => (
          <MenuItem
            key={l.key}
            onClick={() => handleLanguageChange(l.key)}
            value={l.key}
            selected={settings.language === l.key}
          >
            <ListItemText
              primary={t(`languages.${l.key}`)}
              slotProps={{ primary: { fontWeight: 500 } }}
            />
          </MenuItem>
        )),
        showBetaLanguanges ? <Divider key="div1" /> : undefined,
        showBetaLanguanges
          ? betaLanguages.map((l) => (
              <MenuItem
                key={l.key}
                onClick={() => handleLanguageChange(l.key)}
                value={l.key}
                selected={settings.language === l.key}
              >
                <ListItemText
                  primary={t(`languages.${l.key}`)}
                  slotProps={{ primary: { fontWeight: 500 } }}
                />
                <Typography variant="caption" color="text.disabled">
                  {t("common.beta")}
                </Typography>
              </MenuItem>
            ))
          : undefined,
      ]}
    >
      <ListItemIcon>
        <TranslateRoundedIcon sx={{ color: "text.disabled" }} />
      </ListItemIcon>
      <ListItemText
        slotProps={{
          primary: {
            variant: "subtitle1",
            color: "primary.dark",
            gutterBottom: true,
            noWrap: true,
          },
          secondary: { variant: "caption", color: "text.secondary" },
        }}
        primary={t("app.areas.settings.languageLabel")}
        secondary={t("app.areas.settings.languageText")}
      />
      <ArrowRightRoundedIcon
        fontSize="large"
        color="primary"
        sx={({ transitions }) => ({
          transition: transitions.create("transform"),
          transform: open ? "rotate(90deg)" : undefined,
        })}
      />
    </ListItemButtonMenu>
  );
};
